import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useRegisterMutation } from '../features/auth/authApi';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { setCredentials } from '../features/auth/authSlice';
import InlineError from '../components/InlineError';

export default function Register() {
    const [formData, setFormData] = useState({
        companyName: '',
        tenantSlug: '',
        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
        adminPassword: '',
        countryCode: '',
    });

    const [register, { isLoading, error }] = useRegisterMutation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const result = await register(formData).unwrap();
            if (result.verification_required) {
                navigate('/verify-otp', { state: { email: result.email, tenant_slug: result.tenant_slug } });
                return;
            }
            dispatch(setCredentials({ accessToken: result.accessToken!, user: result.user! }));
            navigate('/dashboard', { replace: true });
        } catch (err) {
            // Handled by InlineError
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="w-full max-w-2xl">
                <div className="bg-white shadow-xl rounded-2xl p-8 md:p-12">
                    <div className="text-center mb-10">
                        <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">Create your Workspace</h1>
                        <p className="mt-3 text-lg text-gray-600">Get started with HRMS in minutes</p>
                    </div>

                    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Company Info */}
                        <div className="md:col-span-2 border-b pb-4 mb-2">
                            <h2 className="text-sm font-semibold text-blue-600 uppercase tracking-wider">Company Details</h2>
                        </div>

                        <div>
                            <label htmlFor="companyName" className="form-label">Company Name</label>
                            <input
                                id="companyName"
                                type="text"
                                required
                                className="form-input"
                                placeholder="Acme Inc."
                                value={formData.companyName}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label htmlFor="tenantSlug" className="form-label">Workspace URL Slug</label>
                            <div className="flex">
                                <input
                                    id="tenantSlug"
                                    type="text"
                                    required
                                    className="form-input rounded-r-none border-r-0"
                                    placeholder="acme"
                                    value={formData.tenantSlug}
                                    onChange={handleChange}
                                />
                                <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                    .hrms.com
                                </span>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="countryCode" className="form-label">Country</label>
                            <select
                                id="countryCode"
                                required
                                className="form-input bg-white"
                                value={formData.countryCode}
                                onChange={handleChange}
                            >
                                <option value="">Select Country</option>
                                <option value="OM">Oman</option>
                                <option value="AE">United Arab Emirates</option>
                            </select>
                        </div>

                        {/* Admin Info */}
                        <div className="md:col-span-2 border-b pb-4 mt-4 mb-2">
                            <h2 className="text-sm font-semibold text-blue-600 uppercase tracking-wider">Administrator Information</h2>
                        </div>

                        <div>
                            <label htmlFor="adminFirstName" className="form-label">First Name</label>
                            <input
                                id="adminFirstName"
                                type="text"
                                required
                                className="form-input"
                                placeholder="John"
                                value={formData.adminFirstName}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label htmlFor="adminLastName" className="form-label">Last Name</label>
                            <input
                                id="adminLastName"
                                type="text"
                                required
                                className="form-input"
                                placeholder="Doe"
                                value={formData.adminLastName}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label htmlFor="adminEmail" className="form-label">Work Email</label>
                            <input
                                id="adminEmail"
                                type="email"
                                required
                                className="form-input"
                                placeholder="john@acme.com"
                                value={formData.adminEmail}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label htmlFor="adminPassword" className="form-label">Password</label>
                            <input
                                id="adminPassword"
                                type="password"
                                required
                                className="form-input"
                                placeholder="••••••••"
                                value={formData.adminPassword}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="md:col-span-2 mt-6">
                            <InlineError message={(error as any)?.data?.error?.message || (error as any)?.data?.message || (error as any)?.error} />

                            <button type="submit" disabled={isLoading} className="btn-primary w-full py-3 text-lg">
                                {isLoading ? 'Creating Workspace...' : 'Create Account'}
                            </button>
                        </div>
                    </form>

                    <p className="mt-8 text-center text-sm text-gray-600">
                        Already have an account?{' '}
                        <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                            Sign in here
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
